import React, { useState } from 'react';
import useAuthStore from '@/store/useAuthStore';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface TokenUsageChartProps {
  data: any[];
  title?: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const CustomTooltip = ({ active, payload, label }: any) => {
  const role = useAuthStore((state: any) => state.role);
  const isSuperAdmin = role === 'SUPER_ADMIN';

  if (active && payload && payload.length) {
    let tokenUsed = 0;
    const items = payload.map((p: any, i: number) => {
      if (p.dataKey === 'tokenused') {
        tokenUsed = p.value;
      }
      return (
        <div key={i} style={{ color: p.color }}>
          {p.name}: {p.value}
        </div>
      );
    });

    const cost = ((tokenUsed / 1000) * 0.015).toFixed(4);

    return (
      <div className="bg-surface border shadow-md p-3 rounded" style={{ borderColor: 'var(--color-border-grey)', minWidth: '200px' }}>
        <p className="font-semibold mb-2">{label}</p>
        {items}
        {isSuperAdmin && (
          <div style={{ color: '#FFBB28', marginTop: '8px', borderTop: '1px solid var(--color-divider, rgba(255,255,255,0.1))', paddingTop: '6px' }}>
            <div className="font-semibold text-sm">Azure OpenAI (GPT-5)</div>
            <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
              Calculation: ({tokenUsed} / 1,000) × $0.015
            </div>
            <div className="font-bold mt-1">
              Estimated Cost: ${cost}
            </div>
          </div>
        )}
      </div>
    );
  }
  return null;
};

const TokenUsageChart: React.FC<TokenUsageChartProps> = ({ data, title = "Token Usage" }) => {
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>('bar');

  const xKey = data && data.length > 0 && data[0].month ? 'month' : 'date';

  const renderChart = () => {
    if (!data || data.length === 0) return <div className="p-4 text-center">No data available</div>;

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey={xKey} />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip content={<CustomTooltip />} cursor={{fill: 'transparent'}} />
              <Legend />
              <Bar yAxisId="left" dataKey="tokenused" name="Tokens Used" fill="#00C49F" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="used" name="Requests" fill="#0088FE" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xKey} />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="tokenused" name="Tokens Used" stroke="#00C49F" strokeWidth={2} activeDot={{ r: 8 }} />
              <Line yAxisId="right" type="monotone" dataKey="used" name="Requests" stroke="#0088FE" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                dataKey="tokenused"
                nameKey={xKey}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#000000"
                label
              >
                {data.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-surface rounded-lg p-6 shadow-sm border mt-4 w-full" style={{ borderColor: 'var(--color-border-grey)' }}>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-bold text-foreground">{title}</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setChartType('bar')}
            className={`px-3 py-1 text-sm rounded cursor-pointer text-white`}
            style={chartType === 'bar' ? { backgroundColor: '#000000' } : { border: '1px solid var(--color-border-grey)' }}
          >
            Bar
          </button>
          <button
            onClick={() => setChartType('line')}
            className={`px-3 py-1 text-sm rounded cursor-pointer text-white`}
            style={chartType === 'line' ? { backgroundColor: '#000000' } : { border: '1px solid var(--color-border-grey)' }}
          >
            Line
          </button>
          <button
            onClick={() => setChartType('pie')}
            className={`px-3 py-1 text-sm rounded cursor-pointer text-white`}
            style={chartType === 'pie' ? { backgroundColor: '#000000' } : { border: '1px solid var(--color-border-grey)' }}
          >
            Pie
          </button>
        </div>
      </div>
      {renderChart()}
    </div>
  );
};

export default TokenUsageChart;
